/**
 * Datasets — Inteligência de Dados (Fase 2)
 * -----------------------------------------------------------------------------
 * Listagem e gestão dos conjuntos de dados importados de sistemas externos.
 * Cada dataset aponta para uma conexão + operação e mantém seus registros em
 * armazenamento genérico (JSONB) com controle de geração.
 *
 * A importação nunca roda no navegador: o botão apenas enfileira a execução,
 * processada pelo worker PGMQ em páginas (sem pico de processamento).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 2 - Dados Externos).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database,
  Ban,
  Eraser,
  History,
  Layers,
  Loader2,
  Pencil,
  Play,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  ListPageFiltersBar,
  ListPageContent,
  KpiCard,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DatasetFormDialog } from '@/components/data/DatasetFormDialog';
import {
  useExternalDatasets,
  type ExternalDataset,
} from '@/hooks/data/useExternalDatasets';

/** Aparência dos status devolvidos pela RPC */
const STATUS_META: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  ready: { label: 'Pronto', variant: 'default' },
  never_imported: { label: 'Nunca importado', variant: 'secondary' },
  running: { label: 'Importando', variant: 'outline' },
  error: { label: 'Com erro', variant: 'destructive' },
};

/** Formata data/hora no padrão pt-BR */
const formatDateTime = (value: string | null) =>
  value ? format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '—';

/** Página "Datasets" do módulo Inteligência de Dados */
export default function Datasets() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExternalDataset | null>(null);
  const [deleting, setDeleting] = useState<ExternalDataset | null>(null);
  const [clearing, setClearing] = useState<ExternalDataset | null>(null);

  const { datasets, isLoading, deleteDataset, startImport, cancelImport, clearData } =
    useExternalDatasets({ search });

  /** KPIs calculados no cliente (lista pequena por natureza) */
  const kpis = useMemo(() => {
    const total = datasets.length;
    const records = datasets.reduce((sum, d) => sum + Number(d.record_count ?? 0), 0);
    const running = datasets.filter((d) => d.running_run_id).length;
    return { total, records, running };
  }, [datasets]);

  /** Abre o modal em modo criação */
  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  /** Abre o modal em modo edição */
  const handleEdit = (dataset: ExternalDataset) => {
    setEditing(dataset);
    setFormOpen(true);
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Datasets"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="xs"
              className="gap-1.5"
              onClick={() => navigate('/tenant/data/sync-history')}
            >
              <History />
              Histórico
            </Button>
            <Button size="xs" onClick={handleCreate} className="gap-1.5">
              <Plus />
              Novo dataset
            </Button>
          </div>
        }
      />

      <ListPageKpis className="border-b-0 pb-1">
        <KpiCard color="blue" icon={Layers} label="Datasets" value={kpis.total} />
        <KpiCard
          color="purple"
          icon={Database}
          label="Registros importados"
          value={kpis.records.toLocaleString('pt-BR')}
        />
        <KpiCard color="orange" icon={Loader2} label="Importando agora" value={kpis.running} />
      </ListPageKpis>

      <ListPageFiltersBar className="border-b-0 bg-transparent">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            maxLength={100}
            placeholder="Buscar dataset..."
            className="pl-8"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : datasets.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Database className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum dataset configurado</p>
            <p className="text-xs text-muted-foreground">
              Crie um dataset para importar dados de um sistema já conectado.
            </p>
            <Button variant="outline" onClick={handleCreate} className="mt-2 gap-1.5">
              <Plus className="h-4 w-4" />
              Novo dataset
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {datasets.map((dataset) => {
              const meta = STATUS_META[dataset.status] ?? STATUS_META.never_imported;
              const isRunning = Boolean(dataset.running_run_id);

              return (
                <div
                  key={dataset.id}
                  className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  {/* Identificação */}
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium">{dataset.name}</span>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                      <Badge variant="outline">{dataset.provider_name}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {dataset.connection_name} · {dataset.operation_code} ·{' '}
                      {Number(dataset.record_count ?? 0).toLocaleString('pt-BR')} registros ·{' '}
                      {dataset.fields_count} campos
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Última importação: {formatDateTime(dataset.last_import_at)}
                    </p>
                  </div>

                  {/* Ações */}
                  <div className="flex shrink-0 items-center gap-1">
                    {isRunning ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={cancelImport.isPending}
                            onClick={() => cancelImport.mutate(dataset.running_run_id as string)}
                          >
                            <Ban className="h-4 w-4" />
                            Cancelar
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancelar importação em andamento</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={startImport.isPending}
                            onClick={() => startImport.mutate(dataset.id)}
                          >
                            <Play className="h-4 w-4" />
                            Importar
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Importar agora (roda em segundo plano)</TooltipContent>
                      </Tooltip>
                    )}

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => setClearing(dataset)}>
                          <Eraser className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Limpar dados importados</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(dataset)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Editar dataset</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => setDeleting(dataset)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Excluir dataset</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ListPageContent>

      {/* Modal de criação/edição */}
      <DatasetFormDialog open={formOpen} onOpenChange={setFormOpen} dataset={editing} />

      {/* Confirmação — limpar dados */}
      <AlertDialog open={Boolean(clearing)} onOpenChange={(open) => !open && setClearing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar dados de "{clearing?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os registros importados deixam de aparecer imediatamente. A remoção física é
              feita em segundo plano, em lotes, sem impacto na performance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (clearing) clearData.mutate(clearing.id);
                setClearing(null);
              }}
            >
              Limpar dados
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmação — excluir dataset */}
      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              O dataset e todos os seus registros importados serão removidos. Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleting) deleteDataset.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListPageLayout>
  );
}
